<template>
  <div
    v-loading="loading && fullLoader"
    class="entity-list"
  >
    <infinite-loading
      v-if="!infiniteScrollDisabled && infiniteScrollPosition === 'top'"
      direction="top"
      @infinite="loadNextPageDebounce"
    />
    <slot name="before-list" />
    <template v-if="items && items.length">
      <span
        v-for="item in items"
        :key="item.id"
      >
        <slot
          name="item"
          :item="item"
        />
      </span>
    </template>
    <template v-else-if="initialLoadDone">
      <div
        v-if="!loading"
        class="no-items"
      >
        {{ noItemsText || $t('entity-list.no-items') }}
      </div>
    </template>
    <slot
      name="list"
      :list="items"
    />
    <slot name="after-list" />
    <div class="bottom-loader" v-loading="loading && !fullLoader"></div>
    <infinite-loading
      v-if="!infiniteScrollDisabled && infiniteScrollPosition === 'bottom'"
      direction="bottom"
      @infinite="loadNextPageDebounce"
    />
    <div class="clearfix" />
  </div>
</template>
<script>
  /* eslint-disable no-throw-literal,no-return-assign */

  import ApiService from '@fps/vue-toolkit/dist/services/api.service';
  import InfiniteLoading from 'vue-infinite-loading';
  import debounce from 'lodash-es/debounce';
  import ResourcePage from './ResourcePage';
  import cloneDeep from 'lodash-es/cloneDeep';

  export default {
    components: {
      InfiniteLoading,
    },
    extends: ResourcePage,
    props: {
      filter: {
        type: Array,
        default: () => [],
      },
      sort: {
        type: Array,
        default: () => [],
      },
      resOptions: {
        type: Object,
        default: () => ({}),
      },
      postOnIndex: {
        type: Boolean,
        default: false,
      },
      noItemsText: {
        type: String,
        default: '',
      },
      disableInfiniteScroll: {
        type: Boolean,
        default: false,
      },
      infiniteScrollPosition: {
        type: String,
        default: 'bottom',
      },
      infiniteScrollDebounceTime: {
        type: Number,
        default: 0,
      },
      fullLoader: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        page: 0,
        lastPage: 1,
        total: 0,
        noMoreItemsAvailable: true,
        refreshing: false,
        items: [],
        apiService: ApiService,
        loadNextPageDebounce: null,
        throttleScrollRequest: false,
        initialLoadDone: false
      };
    },
    computed: {
      infiniteScrollDisabled: {
        get() {
          return this.noMoreItemsAvailable
            || this.disableInfiniteScroll
            || this.throttleScrollRequest;
        },
      },
    },
    mounted() {
      if (!this.resource) {
        throw 'No entity defined in EntityListComponent';
      }

      this.loadNextPageDebounce = debounce(this.loadNextPage, this.infiniteScrollDebounceTime);

      // Load the first page
      this.loadNextPageDebounce();
    },
    methods: {
      // Called when route params are loaded, uses idProperty to load the resource
      onRouteLoaded() {
      },

      /**
       * Does the initial load of data.
       *
       * @returns {Observable<any>}
       */
      doInitialLoad() {
        this.loading = true;
        return new Promise((resolve, reject) => {
          this.loadData(1)
            .then((data) => { this.loading = false; resolve(data); })
            .catch((data) => { this.loading = false; reject(data); });
        });
      },

      /**
       * Loads the current elements data.
       *
       * @param page Page to load.
       * @returns {Observable<any>}
       */
      loadData(page) {
        this.throttleScrollRequest = true;
        const options = {
          page,
          filter: JSON.stringify(this.filter),
          order: JSON.stringify(this.sort),
          ...this.resOptions,
        };
        // Load the current entity for the specified page with the specified filters and sorters
        return this.provider.persistResourceData(
          this.apiService.getData(
            this.resource,
            this.postOnIndex ? options : { params: options },
            { postOnIndex: this.postOnIndex },
          ),
        )
          .then((data) => {
            setTimeout(() => {
              this.throttleScrollRequest = false;
              this.noMoreItemsAvailable = this.page >= this.lastPage;
              this.initialLoadDone = true;
            }, this.infiniteScrollDebounceTime);

            return data;
          });
      },

      /**
       * Update current view with its data.
       *
       * @param response Data loaded via request.
       * @param {boolean} replace If true the current data will replace the exsiting data.
       */
      updateWithNewData(response, replace = false) {
        // Either add new data or replace it
        this.items = replace ? cloneDeep(response.data) : this.items.concat(cloneDeep(response.data));

        // Assign current page from response
        this.page = response.current_page;

        // Assign last page from response
        this.lastPage = response.last_page;

        // Set totals
        this.total = response.total;

        this.$emit('response', response);
      },

      /**
       * Triggered when a refresh event appears.
       *
       */
      doRefresh() {
        // Mark refreshing
        this.refreshing = true;
        this.loading = true;
        this.loadNextPageDebounce.cancel()
        // Load the first page
        this.loadData(1)
          .then(({ data }) => {
            // Then replace the exiting data
            this.updateWithNewData(data, true);
            // Tell the directive refreshing is complete
            // Mark refresh finish
            this.refreshing = false;
            this.loading = false;
          });
      },

      /**
       * Triggered when a infinite scroll event appears.
       *
       * @param {InfiniteScroll} infiniteScroll
       */
      loadNextPage() {
        // If there are more pages to load
        if (this.page < this.lastPage) {
          // We load the next page
          this.loading = true;
          this.loadData(this.page + 1)
            .then(({ data }) => {
              // Then we update the current view with new data
              this.updateWithNewData(data);
              // And we signal the infinite-scroll load complete
              this.loading = false;
            })
            .catch(() => this.loading = false);
        }
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/Core/EntityList.vue